import { MESSAGE_PROTOCOL_VERSION, bridgeMessageSchema } from "@ai-sales/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiRequestError, api, type ActivationContextResult, type ActivationStartResult } from "./api.js";
import { createClientId } from "./client-id.js";
import { CheckIcon, SparklesIcon } from "./icons.js";

const createNonce = () => `${createClientId()}-${Date.now()}`;

interface ActivationErrorMessage {
  title: string;
  message: string;
  action?: string;
}

function activationErrorMessage(cause: unknown): ActivationErrorMessage {
  const code = cause instanceof ApiRequestError ? cause.code : undefined;
  switch (code) {
    case "ACTIVATION_CODE_INVALID":
      return {
        title: "激活码不可用",
        message: "请检查激活码是否完整，或联系产品运营人员确认该激活码尚未使用、撤销或过期。",
        action: "本次校验未消耗激活码，也未修改 Salesforce 数据。"
      };
    case "ACTIVATION_ORG_NOT_RESTRICTED":
      return {
        title: "激活码缺少 Org 绑定",
        message: "该激活码不符合安全要求，请联系产品运营人员为当前 Org 重新签发。",
        action: "本次校验未消耗激活码。重新签发时应绑定本页显示的当前 Org ID。"
      };
    case "ACTIVATION_ORG_MISMATCH":
      return {
        title: "当前 Org 与激活码不匹配",
        message: "此激活码不是为当前 Salesforce Org 签发的。请切换到正确的 Org，或联系产品运营人员重新签发。",
        action: "本次校验未消耗激活码。请将本页显示的当前 Org ID 提供给产品运营人员核对。"
      };
    case "ACTIVATION_ADMIN_MISMATCH":
      return {
        title: "当前用户不是指定的激活管理员",
        message: "请退出当前 Salesforce 账号，并使用激活码中指定的 Org 管理员用户重新进入本页。",
        action: "本次校验未消耗激活码。请将本页显示的当前用户 ID 提供给产品运营人员核对。"
      };
    case "ACTIVATION_CODE_ENVIRONMENT_MISMATCH":
      return {
        title: "Salesforce 环境不匹配",
        message: "该激活码绑定的 Salesforce / Salesforce on Alibaba Cloud 环境与当前 Org 不一致。",
        action: "本次校验未消耗激活码。请联系产品运营人员按当前页面显示的环境重新签发。"
      };
    case "ACTIVATION_TICKET_USED":
    case "ACTIVATION_NONCE_MISMATCH":
      return {
        title: "安全校验已失效",
        message: "当前页面的短时安全凭据已过期或已使用。",
        action: "请刷新 Salesforce 页面后重新输入激活码。"
      };
    case "ACTIVATION_COMPONENT_UPDATE_REQUIRED":
      return {
        title: "AI 助手组件需要更新",
        message: "当前 Salesforce 组件版本无法提供完整的组织激活上下文。",
        action: "请更新产品包中的 AI 助手设置组件后重试。"
      };
    case "ACTIVATION_LOGIN_DOMAIN_INVALID":
      return {
        title: "Salesforce 登录域未通过校验",
        message: "系统无法确认当前页面来自受支持的 Salesforce 登录域。",
        action: "请从 Salesforce 内的“AI 助手设置”重新进入；不要使用复制的外部链接。"
      };
    case "SALESFORCE_ACTIVATION_NOT_CONFIGURED":
      return {
        title: "管理员授权环境尚未就绪",
        message: "平台的 Salesforce OAuth 回调或外部客户端应用尚未完成配置。",
        action: "请联系产品运维人员完成连接配置后重试。"
      };
    default:
      return {
        title: "暂时无法继续激活",
        message: cause instanceof Error ? cause.message : "无法完成激活校验。",
        action: "请检查网络后重试；若问题持续，请联系产品运营人员并提供当前 Org ID 和用户 ID。"
      };
  }
}

function trustedParentOrigin(value: string | null): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const trusted = url.protocol === "https:" && (
      host.endsWith(".salesforce.com") || host.endsWith(".force.com") ||
      host.endsWith(".sfcrmproducts.cn") || host.endsWith(".sfcrmapps.cn")
    );
    return trusted ? url.origin : undefined;
  } catch {
    return undefined;
  }
}

export function SalesforceActivationApp() {
  const nonce = useMemo(createNonce, []);
  const parentOrigin = useMemo(() => trustedParentOrigin(new URLSearchParams(window.location.search).get("parentOrigin")), []);
  const ticketReceived = useRef(false);
  const pendingTicketRequest = useRef<{
    requestId: string;
    timeoutId: number;
    resolve: (ticket: string) => void;
    reject: (cause: Error) => void;
  } | undefined>(undefined);
  const [ticket, setTicket] = useState("");
  const [context, setContext] = useState<ActivationContextResult>();
  const [activationCode, setActivationCode] = useState("");
  const [activation, setActivation] = useState<ActivationStartResult>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ActivationErrorMessage>();

  const requestTicket = useCallback((requestId: string) => {
    if (!parentOrigin || window.parent === window) return;
    window.parent.postMessage({
      type: "agent.ready",
      version: MESSAGE_PROTOCOL_VERSION,
      requestId,
      nonce
    }, parentOrigin);
  }, [nonce, parentOrigin]);

  const requestFreshTicket = useCallback(() => new Promise<string>((resolve, reject) => {
    if (!parentOrigin || window.parent === window) {
      reject(new Error("请从 Salesforce 内重新进入 AI 助手设置。"));
      return;
    }
    const requestId = createClientId();
    const timeoutId = window.setTimeout(() => {
      if (pendingTicketRequest.current?.requestId !== requestId) return;
      pendingTicketRequest.current = undefined;
      reject(new Error("无法刷新当前 Salesforce 安全上下文，请稍后重试。"));
    }, 15_000);
    pendingTicketRequest.current = { requestId, timeoutId, resolve, reject };
    requestTicket(requestId);
  }), [parentOrigin, requestTicket]);

  useEffect(() => {
    if (!parentOrigin || window.parent === window) return;
    const announce = () => requestTicket(createClientId());
    const handler = (event: MessageEvent<unknown>) => {
      if (event.origin !== parentOrigin || event.source !== window.parent) return;
      const parsed = bridgeMessageSchema.safeParse(event.data);
      if (!parsed.success || parsed.data.nonce !== nonce || parsed.data.type !== "salesforce.session.ticket") return;
      const pending = pendingTicketRequest.current;
      if (pending && parsed.data.requestId === pending.requestId) {
        window.clearTimeout(pending.timeoutId);
        pendingTicketRequest.current = undefined;
        setTicket(parsed.data.ticket);
        pending.resolve(parsed.data.ticket);
        return;
      }
      if (ticketReceived.current) return;
      ticketReceived.current = true;
      setTicket(parsed.data.ticket);
      setError(undefined);
      void api.getActivationContext(parsed.data.ticket, nonce)
        .then(setContext)
        .catch((cause) => setError(activationErrorMessage(cause)));
    };
    window.addEventListener("message", handler);
    announce();
    const timer = window.setInterval(() => { if (!ticketReceived.current) announce(); }, 1_500);
    return () => {
      window.removeEventListener("message", handler);
      window.clearInterval(timer);
      const pending = pendingTicketRequest.current;
      if (pending) {
        window.clearTimeout(pending.timeoutId);
        pendingTicketRequest.current = undefined;
        pending.reject(new Error("Salesforce 激活页面已关闭。"));
      }
    };
  }, [nonce, parentOrigin, requestTicket]);

  if (!parentOrigin || window.parent === window) {
    return <main className="activation-shell"><section className="activation-card">
      <div className="admin-login-brand"><SparklesIcon /><div><h1>Salesforce AI 助手</h1><p>组织设置</p></div></div>
      <h2>请从 Salesforce 打开</h2>
      <p className="muted">首次激活必须从已安装产品包的 Salesforce Org 内发起，以便安全校验 Org 和管理员身份。</p>
    </section></main>;
  }

  const activated = context && context.status !== "not_activated";
  const submit = async () => {
    if (!ticket) return;
    setBusy(true);
    setError(undefined);
    try {
      const freshTicket = await requestFreshTicket();
      setActivation(await api.startActivation(activationCode, freshTicket, nonce));
    } catch (cause) {
      setError(activationErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return <main className="activation-shell"><section className="activation-card">
    <div className="admin-login-brand"><SparklesIcon /><div><h1>Salesforce AI 助手</h1><p>组织设置</p></div></div>
    {!context && !error ? <div className="activation-loading"><span className="admin-spinner" /><h2>正在读取当前组织</h2><p>正在通过 Salesforce 签名上下文确认 Org 和当前用户。</p></div> : null}
    {error ? <div className="activation-error" role="alert">
      <strong>{error.title}</strong>
      <p>{error.message}</p>
      {error.action ? <small>{error.action}</small> : null}
    </div> : null}
    {context ? <>
      <div className="activation-org-summary">
        <span className={activated ? "connection-pill connection-pill--ok" : "connection-pill connection-pill--warn"}>
          {activated ? "产品已激活" : "等待首次激活"}
        </span>
        <dl><div><dt>当前 Org</dt><dd><code>{context.organizationId}</code></dd></div>
          <div><dt>当前用户</dt><dd><code>{context.salesforceUserId}</code></dd></div>
          <div><dt>Salesforce 环境</dt><dd>{context.environmentLabel}</dd></div></dl>
      </div>
      {activated ? <div className="activation-complete">
        <span><CheckIcon size={24} /></span><h2>{context.status === "active" ? "AI 助手已启用" : "产品授权需要处理"}</h2>
        <p>{context.status === "active"
          ? context.isPrimaryAdmin ? "您是该组织的首位管理员，可以进入管理中心继续配置 AI 用户和业务场景。" : "当前组织已激活。普通用户的可用范围由管理中心的 AI 用户授权决定。"
          : `当前授权状态：${context.status}。请联系产品运营人员处理后重试。`}</p>
        {context.status === "active" && context.isPrimaryAdmin ? <a className="button button--primary button--full" href={`/admin?parentOrigin=${encodeURIComponent(parentOrigin)}`} target="_self">在 Salesforce 中打开管理中心</a> : null}
        {context.status === "active" ? <a className="button button--secondary button--full" href="/workspace" target="_top">打开独立 Web 端</a> : null}
      </div> : activation ? <div className="activation-complete">
        <span><CheckIcon size={24} /></span><h2>产品授权校验通过</h2>
        <p>下一步将使用当前 Salesforce 管理员完成 OAuth 授权。授权回调会再次核对 Org ID 和 User ID。</p>
        <dl><div><dt>客户</dt><dd>{activation.target.customerName}</dd></div>
          <div><dt>授权环境</dt><dd>{activation.target.environmentLabel}</dd></div></dl>
        <a className="button button--primary button--full" href={activation.authorizationUrl} target="_top" rel="noreferrer">继续 Salesforce 管理员授权</a>
        <button className="text-button" type="button" onClick={() => setActivation(undefined)}>返回修改激活码</button>
      </div> : <div className="admin-login-form">
        <div className="activation-intro">
          <p className="activation-eyebrow">首次启用</p>
          <h2>激活当前 Salesforce 组织</h2>
          <p>请使用<strong>有效激活码中指定的 Org 管理员账号</strong>完成操作。系统将自动交叉校验当前 Org ID、当前用户 ID 和 Salesforce 环境。</p>
        </div>
        <div className="activation-checklist" aria-label="激活前检查">
          <strong>激活前请确认</strong>
          <ul>
            <li>激活码有效、未使用、未撤销且未过期</li>
            <li>当前 Org ID 与激活码绑定的 Org 完全一致</li>
            <li>当前用户是激活码中指定的首位管理员</li>
          </ul>
        </div>
        <label>产品激活码<input
          aria-describedby="activation-code-help"
          autoComplete="off"
          spellCheck={false}
          value={activationCode}
          onChange={(event) => { setActivationCode(event.target.value.replace(/\s/g, "").toUpperCase()); setError(undefined); }}
          placeholder="AIA-ORG-AUTO-…"
        /></label>
        <small id="activation-code-help" className="activation-field-help">粘贴产品运营人员提供的完整一次性激活码。</small>
        <button className="button button--primary button--full" type="button" disabled={busy || activationCode.trim().length < 20 || !ticket} onClick={() => void submit()}>{busy ? "正在校验…" : "验证激活码并继续"}</button>
        <div className="activation-safety-note"><strong>校验失败不会消耗激活码</strong><span>也不会创建平台用户、写入 Salesforce 业务数据或改变当前 Org 授权。</span></div>
        <small className="admin-login-note">校验和管理员 OAuth 授权都通过后，当前用户才会成为该 Org 的首位 AI 平台管理员。</small>
      </div>}
    </> : null}
  </section></main>;
}
